import { formatNumber as ngFormatNumber } from '@angular/common';
import { computed, ElementRef, Renderer2, Signal } from '@angular/core';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ParameterOption, QuotePair } from '@approot/models';
import { FeatureFlag } from '@root/environments/environment.type';
import { cloneDeep, get, isEqual, once, random, shuffle } from 'lodash';
import { environment } from 'src/environments/environment';

export type FX_API_SUB_PATHS =
  | 'strike'
  | 'schedule'
  | 'pricing'
  | 'get-user-info'
  | 'marketDataService'
  | 'tradeService'
  | 'dateService'
  | 'next-business-day'
  | 'expiration-date-from-settlement-dates'
  | 'expiration-settlement-dates'
  | 'validate_rfq'
  | 'medusaBookTrades'
  | 'liveMarketData'
  | 'allocationsAvailableSubAccounts'
  | 'aggregateResults'
  | 'whiteLabelServices'
  | 'spot'
  | 'breakDownVanillaStrategy'
  | 'getRfqs'
  | 'adjustQuoteForMargin'
  | 'rfq/recordPickedUpEvent'
  | 'rfq/submitSetupEvent'
  | 'rfq/submitPricingUpdateRequestEvent'
  | 'handleDealerIntervention';

export type FX_API_PATHS = `/fx/${FX_API_SUB_PATHS}`;

export type EXOTICS_API_PATHS =
  | '/exotics/raygunproxy'
  | '/exotics/exoticsssoauth';

export type API_PATHS = FX_API_PATHS | EXOTICS_API_PATHS;

export type EndOfTextboxType = 'percentage' | 'currency' | 'pips';
export class Utilities {
  static customText = 'Custom';
  static orangeTextColor = 'var(--orange-text-color)';
  static whiteTextColor = 'var(--pure-white)';
  static greyTextColor = 'grey';
  static greenTextColor = 'var(--positive-green)';
  static redTextColor = 'var(--negative-red)';
  static blueTextColor = 'var(--near-turquoise)';

  static randomNumber(topNumber: number) {
    return Math.floor(Math.random() * topNumber + 1);
  }
  // convert a number
  static abbreviateNumber(value) {
    var returnValue = value;
    var negative = Math.sign(value) < 0 ? true : false;
    value = Math.abs(value);
    if (value >= 1000) {
      var suffixes = ['', 'k', 'm', 'b', 't'];
      var suffixNum = Math.floor(('' + value).length / 3);
      var shortValue: any;
      for (let precision = 2; precision >= 1; precision--) {
        shortValue = parseFloat(
          (suffixNum !== 0
            ? value / Math.pow(1000, suffixNum)
            : value
          ).toPrecision(precision)
        );
        var dotLessShortValue = (shortValue + '').replace(
          /[^a-zA-Z 0-9]+/g,
          ''
        );
        if (dotLessShortValue.length <= 2) {
          break;
        }
      }
      if (shortValue % 1 !== 0) shortValue = shortValue.toFixed(1);
      let sign = negative ? '-' : '';
      returnValue = sign + shortValue + suffixes[suffixNum];
    }
    return returnValue;
  }

  static isValidDelta(delta: string) {
    if (delta[delta.length - 1] === 'd' && delta.length > 1) {
      var newStr = delta.substring(0, delta.length - 1);
      if (newStr.length <= 2 && !isNaN(parseInt(newStr))) {
        return true;
      } else return false;
    }
    return false;
  }

  static removeCommasConvertToNumber(value) {
    return isNaN(value) ? Number(value.replace(/,/g, '')) : Number(value);
  }

  static isNumberFormatted(value, allowNegative?: boolean) {
    let valid = new RegExp(
      '^' + (allowNegative ? '-?' : '') + '\\d{1,3}(,\\d{3})*(\\.\\d+)?$'
    ).test(value);
    return valid;
  }

  static isMillionShorthand(value, allowNegative?: boolean) {
    return new RegExp(
      '^' +
        (allowNegative ? '-?' : '') +
        '(?:(\\d{3}m|\\d{2}m|\\dm|[0-9]+([.][0-9]+)m))$'
    ).test(value);
  }
  static isThousandShorthand(value, allowNegative?: boolean) {
    return new RegExp(
      '^' +
        (allowNegative ? '-?' : '') +
        '(?:(\\d{3}k|\\d{2}k|\\dk|[0-9]+([.][0-9]+)k))$'
    ).test(value);
  }

  static generateSchedule(product): any {
    let scheduleArray = [];
    product.schedule.forEach(date => {
      scheduleArray.push({
        fixingDate: date.fixingDate,
        settlementDate: date.settlementDate,
        notional: parseFloat(product.notional.replace(/,/g, '')),
        leverage: product.leverage,
        strike: product.strike,
        EKI: product.barrier
      });
    });

    return scheduleArray;
  }

  static isNotBlank(value: any): boolean {
    return value != null && value !== '';
  }

  static generateBasket(rowData?: any): FormGroup {
    const basketRow = new FormGroup({
      cross: new FormControl(
        rowData && rowData.cross ? rowData.cross : '',
        Validators.required
      ),
      weight: new FormControl(
        rowData && rowData.weight ? rowData.weight : '',
        Validators.required
      ),
      power: new FormControl(
        rowData && rowData.power ? rowData.power : '',
        Validators.required
      )
    });

    return basketRow;
  }
  static generateOption(rowData?: any): FormGroup {
    const optionRow = new FormGroup({
      currencyPair: new FormControl(
        rowData && rowData.currencyPair ? rowData.currencyPair : '',
        Validators.required
      ),
      position: new FormControl(
        rowData && rowData.position ? rowData.position : '',
        Validators.required
      ),
      productType: new FormControl(
        rowData && rowData.productType ? rowData.productType : '',
        Validators.required
      ),
      optionType: new FormControl(
        rowData && rowData.optionType ? rowData.optionType : '',
        Validators.required
      ),
      currency: new FormControl(
        rowData && rowData.currency ? rowData.currency : '',
        Validators.required
      ),
      notional: new FormControl(
        rowData && rowData.notional ? rowData.notional : '',
        Validators.required
      ),
      strike: new FormControl(
        rowData && rowData.strike ? rowData.strike : '',
        Validators.required
      ),
      barrier: new FormControl(
        rowData && rowData.barrier ? rowData.barrier : '',
        Validators.required
      )
    });

    this.optionPortfolioDisableOrEnableBarrier(
      optionRow.get('productType').value,
      optionRow
    );

    return optionRow;
  }

  static optionPortfolioDisableOrEnableBarrier(
    productType: string,
    optionRow: FormGroup
  ) {
    if (productType === 'European KI') {
      optionRow.get('barrier').enable();
    } else {
      optionRow.get('barrier').disable();
    }
  }

  static shortHandNumberConverter(
    value: string,
    allowNegative?: boolean
  ): number {
    if (this.isThousandShorthand(value, allowNegative)) {
      return Number(value.replace('k', '')) * 1_000;
    } else if (this.isMillionShorthand(value, allowNegative)) {
      return Number(value.replace('m', '')) * 1_000_000;
    } else {
      return +value;
    }
  }

  static containsAtEndChecker(
    value: string,
    contains: EndOfTextboxType
  ): boolean {
    // function used to see if a value contains either a currency or a percentage at the end
    switch (contains) {
      case 'percentage':
        return /^%$/.test(value);
      case 'currency':
        return /^[\s][A-Z]{3}([\s]per[\s](leg|fixing))?$/.test(value);
      // regex OR for per leg/fixing after currency abv.
      case 'pips':
        return /^[\s]pips$/.test(value);
      default:
        return false;
    }
  }

  static getIndexOfSeparator(input, separator) {
    // used to get the index of the item separating editable parts of text and non editable parts
    if (new RegExp('^.*( )[A-Z]{3}(.*)?$').test(input)) {
      // needed to prevent things like ' ' triggering as a separator being needed
      return input.indexOf(separator);
    } else if (new RegExp('^.*%$').test(input)) {
      return input.indexOf(separator);
    } else {
      return -1; // no separator so no split
    }
  }

  static notionalCurrencyRemover(inputVal) {
    // removes the notional currency
    let value = inputVal;
    // Note validation occurs before base currency is added or removed from text box
    if (
      value !== undefined &&
      value.length >= 4 &&
      Utilities.containsAtEndChecker(
        value.substring(Math.max(0, value.indexOf(' '))),
        'currency'
      )
    ) {
      value = value.substring(0, value.indexOf(' ')); // if the base currency is involved with the control value, remove it
      return value;
    } else {
      return inputVal;
    }
  }

  static currentEnvironment() {
    switch (environment.api_domain) {
      case 'api':
        return 'prod';
      case 'api-dev':
        return 'dev';
      case 'api-uat':
        return 'uat';
      default:
        return null;
    }
  }

  static formattedNumberToNumber(value: string | number): number {
    if (typeof value === 'string') {
      return parseFloat(value.replace(/,/g, ''));
    }
    return value;
  }

  static tableToExcelFormat(table: ElementRef) {
    // The reason for having a tableIntoExcelFormat is because in certain cases, like the schedule popup,
    // the underlying data has been piped, meaning the appearance of the data is different in reality.
    // so we cant just copy the array to the clipboard.

    let copyToClipBoard = ''; // this is where we will place the text to be copied

    // we need to get references to the table headers and table data

    // we need to run through the table headers and add each one to what will be copied.

    // Note: In excel each cell is separated by a tab character, so after each item add one
    //     : Each row is separated by a new line character

    Object.values(table['tHead']['rows'][0]['cells']).forEach(
      (element, index) => {
        copyToClipBoard += element['innerText'];
        if (index < table['tHead']['rows'][0]['cells'].length - 1) {
          // if we are at the last item don't add a tab
          copyToClipBoard += '\t';
        }
      }
    );

    if (table['tBodies'][0]['rows'].length) {
      // if there is rows in the table body then add a new line
      copyToClipBoard += '\n'; // new row
    }
    // // run through each of the table data rows
    Object.values(table['tBodies'][0]['rows']).forEach((element, index) => {
      Object.values(element['cells']).forEach((cell, cellIndex) => {
        if (cell['children'].length === 0) {
          // if a row doesnt have any children, this must mean that it just has an inner text
          copyToClipBoard += cell['innerText'];
        } else {
          // else inside may be a input, so take the value of the element
          copyToClipBoard +=
            cell['children'][0].value ?? cell['children'][0].textContent.trim();
        }

        if (cellIndex < table['tHead']['rows'][0]['cells'].length - 1) {
          // new tab for next cell
          copyToClipBoard += '\t';
        }
      });

      if (index < table['tBodies'][0]['rows'].length - 1) {
        copyToClipBoard += '\n'; // new row
      }
    });

    return copyToClipBoard;
  }

  static arrayToExcelFormat(array: any[]) {
    // the array passed in must be of the format [[row1],[row2],[row3]........]
    let copyToClipBoard = '';

    array.map((row, rowIndex) => {
      row.map((item, itemIndex) => {
        copyToClipBoard += item;
        if (itemIndex < row.length - 1) {
          copyToClipBoard += '\t';
        }
      });
      if (rowIndex < array.length - 1) {
        copyToClipBoard += '\n';
      }
    });
    return copyToClipBoard;
  }

  static percentageToNumber(percentage: string) {
    // turn percentages to numbers e.g. 5% to 0.05

    // converting the number by dividing by 100.
    // we don't do this through normal division as this would cause precision issues
    // so the way we do it, is convert the decimal into an integer and then divide it
    // by a power of 10 to simulate the division of 100.

    // Sometimes percentage is actually a number not a string
    const valSplit = `${percentage}`.replace('%', '').split('.');
    let divide = 100;
    let temp = 0;
    if (valSplit.length === 2) {
      divide = Math.pow(10, valSplit[1].length + 2);
      temp = +(valSplit[0] + valSplit[1]);
    } else {
      temp = +valSplit[0];
    }
    return +temp / divide;
  }

  static endOfTextBoxAdder(
    // this function is used to add an item of text to the end of a text box
    // NOTE: setting addToTextbox to  false removes the ending text
    element: any, // element
    control: AbstractControl, // form control
    textToAdd: string, // the text to be added to the text box
    addToTextbox: boolean, // need to check if this isn't null
    typeAdded: EndOfTextboxType, // the type of the text that is going to be added
    renderer: Renderer2
  ) {
    let currentValue = control.value.toString();
    const justTextEnding = currentValue.substring(
      Math.max(0, currentValue.length - textToAdd.length)
    ); // the ending of the text box, assuming the text box already has the text added to the end
    if (Utilities.containsAtEndChecker(currentValue, typeAdded)) {
      // if string is just ' EUR' or '%' just wipe the whole field
      control.setValue('');
    } else if (addToTextbox && currentValue) {
      // the item to be added, e.g. baseCurrency is checked if its not null
      // text box must have a value, as the text should only be added if text box is already populated

      if (currentValue === Utilities.customText) {
        // If the text is custom don't add to the end of the text
        return;
      }

      if (!Utilities.containsAtEndChecker(justTextEnding, typeAdded)) {
        // if the text isn't already at the end, add it
        currentValue = currentValue + textToAdd;
        control.setValue(currentValue, { emitEvent: false });
      } else if (justTextEnding !== textToAdd) {
        // in case that the text added to the end has changed, proceed to add it
        currentValue =
          currentValue.substring(0, currentValue.length - textToAdd.length) +
          textToAdd; // if it has changed replace last 3 chars to new base Currency
        control.setValue(currentValue, { emitEvent: false });
      } else {
        return;
      }

      if (element) {
        renderer.setProperty(
          element,
          'selectionEnd',
          element.value.indexOf(textSymbolDivider(typeAdded))
        ); // set the text caret to be in the position of just before the added text
      }
    } else if (currentValue && currentValue !== Utilities.customText) {
      // if the text to be added doesn't exist anymore, e.g. currency pair has been wiped

      control.setValue(Utilities.formattedNumberToNumber(currentValue), {
        emitEvent: false
      }); // remove added text
    }
  }

  static textBoxKeydownRestrictor(
    event: any,
    indexLocator: number,
    renderer: any
  ) {
    if (indexLocator > -1) {
      if (
        event.keyCode === 37 ||
        event.keyCode === 38 ||
        event.keyCode === 39 ||
        event.keyCode === 40
      ) {
        // if left, up, right or down direction buttons are pressed
        if (event.target.selectionEnd >= indexLocator) {
          // and caret location is further than non-editable parts of text
          renderer.setProperty(event.target, 'selectionEnd', indexLocator - 1);
        }
      }
    }
  }

  static textBoxClickRestrictor(
    event: any,
    indexLocator: number,
    renderer: any
  ) {
    if (event.target.selectionEnd >= indexLocator) {
      // if where the text caret is, is greater than the index of what shouldn't be changed
      renderer.setProperty(event.target, 'selectionEnd', indexLocator); // set the text cursor location back
    }
  }

  static attributeBelongsToWhichCross(attribute) {
    // will return whether an attributes belongs to cross or second cross
    // attributes here are formatted based on the cross
    switch (attribute) {
      case 'basketStrike':
      case 'spot':
      case 'barrier':
      case 'gliderBarrier':
      case 'upperBarrier':
      case 'lowerBarrier':
      case 'strike':
      case 'strike1':
      case 'strike2':
      case 'nearStrike':
      case 'farStrike':
      case 'gliderStrike':
      case 'vanillaStrike':
      case 'shelterStrike':
      case 'strikeCapOrFloor':
      case 'koBarrier':
      case 'kiBarrier':
      case 'strikeOffset':
      case 'ekiOffset':
      case 'strikeLong':
      case 'pivot':
      case 'lowerPivot':
      case 'upperPivot':
      case 'strikeShort':
      case 'strikeInRange':
      case 'rangeLowerBarrier':
      case 'rangeUpperBarrier':
      case 'callStrike':
      case 'putStrike':
      case 'phoenixLevel':
      case 'trigger':
      case 'lko':
      case 'lkoLowerBarrier':
      case 'lkoUpperBarrier':
      case 'fixing':
      case 'bonusStrike':
      case 'penaltyStrike':
        return 'cross';
      case 'secondSpot':
      case 'secondStrike':
      case 'secondBarrier':
      case 'secondOffset':
      case 'secondEkiOffset':
      case 'secondStrikeOffset':
      case 'secondPivot':
      case 'secondStrikeInRange':
      case 'secondStrikeShort':
      case 'secondLeverageLong':
      case 'secondRangeLowerBarrier':
      case 'secondRangeUpperBarrier':
      case 'secondStrikeLong':
      case 'secondUpperBarrier':
      case 'secondLowerBarrier':
      case 'secondCallStrike':
      case 'secondPutStrike':
      case 'secondLko':
        return 'secondCross';
      default:
        return '';
    }
  }

  static outputUnderlyingValidator(
    fields: ParameterOption[],
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    const type = control.parent.get('type').value;
    if (type) {
      const underlyingValidators = fields.find(
        element => element.longName === type
      );
      const notValid = underlyingValidators.validators.some(validator => {
        switch (validator) {
          case 'required':
            return !control.value;
          default:
            return false;
        }
      });
      return notValid ? { invalidEntry: { value: control.value } } : null;
    } else {
      return null;
    }
  }

  public static get currentTimezone(): string {
    return window.Intl?.DateTimeFormat().resolvedOptions().timeZone;
  }

  public static formatNumber(n: number, dp = 0) {
    return ngFormatNumber(n, 'en-GB', `1.${dp}-${dp}`);
  }

  public static roundNumberToDp(
    value: any,
    dp: number,
    formatNumber = false
  ): string {
    // rounds number to dp if the current decimals places is below the wanted dp
    if (isNaN(value)) {
      return null;
    }

    const dpToUse = Math.max(dp, value.toString().split('.')[1]?.length || 0);
    const roundedValue = value.toFixed(dpToUse);

    return formatNumber
      ? this.formatNumber(roundedValue, dpToUse)
      : roundedValue;
  }

  public static cleanFormattedNumeric(value: string | number): number {
    if (typeof value == 'number') {
      return value;
    }
    if (value == null) {
      return null;
    }
    value = value.replace(/,/g, '');
    return Utilities.shortHandNumberConverter(value);
  }

  public static minmax(n: number, min: number, max: number): number {
    if (max < min) {
      [min, max] = [max, min];
    }
    return Math.min(Math.max(n, min), max);
  }

  public static medusaEnviroment() {
    const env = Utilities.currentEnvironment();
    switch (env) {
      case 'dev':
        return 'devtest';
      default:
        return env;
    }
  }
}

export function parseJWT(token: string): Record<string, any> {
  return JSON.parse(window.atob(token.split('.')[1]));
}

export function handleChoicePrice(
  formGroup: FormGroup,
  fieldName: string,
  newValue: string
): boolean {
  const correspondingSide = getOppositePosition(fieldName);
  const ctrl = formGroup.get(fieldName);
  if (ctrl.value.toString().toLowerCase().includes('ch') && correspondingSide) {
    const correspondingCtrl = formGroup.get(correspondingSide);
    ctrl.setValue(newValue);
    correspondingCtrl?.markAsTouched();
    correspondingCtrl?.setValue(newValue);
    return true;
  }
  return false;
}

export function apiURL(path?: API_PATHS, qs?: Record<string, string>): string {
  const base = `https://${environment.api_domain}.fxexotics.com`;
  if (path === undefined) {
    return base;
  }
  let str = '';
  if (qs) {
    str = Object.entries(qs)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    str = str ? `?${str}` : '';
  }
  return `${base}${path}${str}`;
}

export function getUserInfoURL(task: string): string {
  return apiURL('/fx/get-user-info', { task });
}

export function camelToKebabCase(str: string) {
  return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function getOppositePosition(field: string): string {
  if (field.includes('Ask')) {
    return field.replace('Ask', 'Bid');
  } else if (field.includes('Bid')) {
    return field.replace('Bid', 'Ask');
  }

  return null;
}

// clear out fields with falsy values, including 0 (zero) but 'false' is allowed
export function clearEmptyValues<T>(obj: T): T {
  obj = { ...obj }; // shallow copy
  Object.entries(obj).forEach(([k, v]) => {
    if (v === false) {
      // pass
    } else if (!v) {
      delete obj[k];
    }
  });
  return obj;
}

const MAX_CLONE_DEPTH = 10;

export function deepFreeze<T>(obj: T, depth = 0): Readonly<T> {
  if (depth > MAX_CLONE_DEPTH) {
    throw new Error('deepFreeze recursion depth exceeded');
  }
  // Freeze properties before freezing self
  Object.getOwnPropertyNames(obj).forEach((name: string) => {
    const prop = obj[name];

    // Freeze prop if it is an object. (Arrays are objects too, as is null)
    if (prop && typeof prop === 'object') {
      deepFreeze(prop, depth + 1);
    }
  });

  // Freeze and return self (no-op if already frozen)
  return Object.freeze(obj) as Readonly<T>;
}

export function clone<T>(obj: T, freeze = false): T {
  const clone = cloneDeep<T>(obj);
  return freeze ? deepFreeze<T>(clone) : clone;
}

export function isFeatureEnabled(
  feature: FeatureFlag,
  defaultValue: boolean
): boolean | undefined {
  let ret: boolean | undefined;
  ret = get(environment.featureFlags, feature);
  if (ret === undefined) {
    return defaultValue;
  }
  return !!ret;
}

export function getFeature(feature: FeatureFlag, defaultValue: any): any {
  let ret: any;
  ret = get(environment.featureFlags, feature) || defaultValue;
  return ret;
}

export function whatis(value: any) {
  return Object.prototype.toString
    .call(value)
    .replace(/^\[object\s+([a-z]+)\]$/i, '$1')
    .toLowerCase();
}

function textSymbolDivider(divider: EndOfTextboxType): '%' | ' ' {
  switch (divider) {
    case 'percentage':
      return '%';
    case 'currency':
    case 'pips':
      return ' ';
  }
}

export const AgGridColorPalette = [
  '#058DC7',
  '#50B432',
  '#ED561B',
  '#DDDF00',
  '#24CBE5',
  '#64E572',
  '#FF9655',
  '#FFF263',
  '#6AF9C4'
];

export function uniq<T>(arr: T[]): T[] {
  return Array.from<T>(new Set<T>(arr));
}

export function crossToPair(cross: string): QuotePair {
  return {
    base: cross.substring(0, 3),
    term: cross.substring(3, 6)
  };
}

// invoke as pipe(f1,f2,...,fN)(data), equivalent to f3(f2(f1(data))) etc.
// Custom functions should be curried, e.g., via lodash/fp/curry
export function pipe<T>(...fns: Array<(arg: T) => T>) {
  return (value: T) => fns.reduce((acc, fn) => fn(acc), value);
}

// pipeline(data,f1,f2,...,fN) equivalent to pipe(f1,f2,...,fN)(data)
export function pipeline<T>(data: T, ...fns: Array<(arg: T) => T>) {
  return pipe(...fns)(data);
}

// bind all functions to thisObj (ala fn.bind(this)) before pipelining
export function pipelineBindAll<T>(thisObj: any) {
  return function (data: T, ...fns: Array<(arg: T) => T>) {
    fns = bindAll(thisObj, fns);
    return pipe(...fns)(data);
  };
}

// can inject into pipeline to debug
export function inspect<T>(label: string): (arg: T) => T {
  return function (o: any) {
    console.log(label, clone(o));
    return o;
  };
}

export function bindAll<T>(thisObj: any, fns: Array<(arg: T) => T>) {
  return fns.map(fn => fn.bind(thisObj));
}

/**
 * Naive function to deep clear nullish values
 */
export function clearNullishValues<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return (obj as any[]).map((v: T) =>
      v && typeof v === 'object' ? clearNullishValues<T>(v) : v
    ) as unknown as T;
  } else {
    return Object.entries(obj)
      .map(([k, v]) => [
        k,
        v && typeof v === 'object' ? clearNullishValues<T>(v) : v
      ])
      .reduce((a, [k, v]) => (v == null ? a : ((a[k] = v), a)), {} as T);
  }
}

// Safely sum array of numbers.
export function sum(arr: number[]): number {
  return arr.reduce((acc, val) => acc + (Number.isFinite(val) ? val : 0), 0);
}

export function shortUuid(maxLen = 16): string {
  return uuid().replace(/-/g, '').substring(0, maxLen);
}

export function uuid(): string {
  // randomUUID is only available in "secure contexts" e.g., https
  if (window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }

  // Fallback in certain local modes.
  // Template is overkill - doesn't need to be fully UUID4 compliant.
  if (!isFeatureEnabled('localDev', false)) {
    console.warn('Using fallback UUID generation');
  }
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  let rArray = window.crypto.getRandomValues(new Uint8Array(template.length));

  const id = template.replace(/[xy]/g, function (c, i) {
    var r = rArray[i],
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  return id;
}

export const sessionId = once(function (len = 16): string {
  const arr = Array.from(window.crypto.getRandomValues(new Uint8Array(len)));
  return arr.map(v => (v & 0x0f).toString(16)).join('');
});

// Many Hydra API return strings ('true' or 'false') for boolean values.
export function isTrueLike(val: string | boolean | null | undefined): boolean {
  return val === true || val === 'true' || val === 'True' || val === '1';
}

export function isSellPosition(position: string): boolean {
  return (
    position === 'Sell' ||
    position === 'Unwind' ||
    position === 'Pay' ||
    position === 'Sell Calls / Buy Puts'
  );
}

export function isBuyPosition(position: string): boolean {
  return (
    position === 'Buy' ||
    position === 'Enter' ||
    position === 'Receive' ||
    position === 'Buy Calls / Sell Puts'
  );
}

export function formatPremium(
  val: number,
  premiumStyle: string,
  abs = false
): string {
  let suffix = '';
  let format = '1.2-2';
  try {
    if (premiumStyle === 'percent') {
      format = `1.4-4`;
      suffix = '%';
    } else if (premiumStyle === 'Amount') {
      //
    } else {
      suffix = ' ' + premiumStyle;
    }

    if (abs) {
      val = Math.abs(val);
    }
    const strVal = val.toString().replace(/,/g, '');
    return ngFormatNumber(+strVal, 'en-GB', format) + suffix;
  } catch {
    return `${val}`;
  }
}

export function initialCaps(str: string): string {
  const words = str.split(' ');
  for (let i = 0; i < words.length; i++) {
    if (words[i].length === 0) {
      continue;
    }
    const firstLetter = words[i].charAt(0);
    words[i] = firstLetter.toUpperCase() + words[i].slice(1);
  }
  return words.join(' ');
}

/**
 * AWS rules for password:
 *     Length at least 16 characters
 *     Contains at least 1 number
 *     Contains at least 1 special character
 *     Contains at least 1 uppercase letter
 *     Contains at least 1 lowercase letter
 *     Special characters are:
 *        ^$*.[]{}()?-"!@#%&/\,><':;|_~`+=
 *     but we only use a subset
 *
 * N.B. By default this is not a cryptographically secure password generator
 * as lodash uses Math.random().
 *
 * However, Math.random() should have been polyfilled to use a better version.
 * See polyfills.ts
 */
export function generateRandomAWSPassword(pwLen = 16): string {
  function c(chars: Readonly<string>, length: number): string[] {
    return Array.from({ length }, () => shuffle(chars)[0]);
  }

  // Exclude 0/O & 1/I/l to avoid visual confusion
  const num = c('23456789', random(2, 4));
  const special = c('!@#$%^&*()_+', random(1, 2));
  const uc = c('ABCDEFGHJKLMNPQRSTUVWXYZ', random(2, 4));
  let password = [...num, ...special, ...uc];
  const pad = c('abcdefghijkmnopqrstuvwxyz', pwLen - password.length);
  password = shuffle([...password, ...pad]);
  if (password.length !== pwLen) {
    console.error(
      `Password generation error - wrong length. Expected ${pwLen} got ${password.length}`
    );
  }
  return password.join('');
}

/**
 * Convenience function to create a computed signal with the isEqual comparator.
 */
export function computedSignal<T>(fn: () => T): Signal<T> {
  return computed<T>(fn, { equal: isEqual });
}

export function medusaEnviroment() {
  switch (Utilities.currentEnvironment()) {
    case 'dev':
      return 'devtest';
    case 'uat':
      return 'uat';
    case 'prod':
      return 'prod';
  }
}

export function exponentialDelay(iteration: number): number {
  const delay = Math.max(500, 2 ** iteration * 100);
  return delay >= 10_000 ? 10_000 : delay;
}

export function splitStringByMaxLength(
  inputString: string,
  maxLength: number,
  splitBy = /\s+/g
): string[] {
  const words = inputString.split(splitBy);
  const result = [];
  let currentPart = '';

  for (const word of words) {
    if (currentPart.length + word.length <= maxLength) {
      // Add the word to the current part
      currentPart += (currentPart ? ' ' : '') + word;
    } else {
      // Start a new part
      result.push(currentPart);
      currentPart = word;
    }
  }

  // Add the last part (if any)
  if (currentPart) {
    result.push(currentPart);
  }

  return result;
}

// https://github.com/douglascrockford/JSON-js/blob/master/cycle.js
export function decycle(object: any, replacer?) {
  // Make a deep copy of an object or array, assuring that there is at most
  // one instance of each object or array in the resulting structure. The
  // duplicate references (which might be forming cycles) are replaced with
  // an object of the form

  //      {"$ref": PATH}

  // where the PATH is a JSONPath string that locates the first occurance.

  // So,

  //      var a = [];
  //      a[0] = a;
  //      return JSON.stringify(JSON.decycle(a));

  // produces the string '[{"$ref":"$"}]'.

  // If a replacer function is provided, then it will be called for each value.
  // A replacer function receives a value and returns a replacement value.

  // JSONPath is used to locate the unique object. $ indicates the top level of
  // the object or array. [NUMBER] or [STRING] indicates a child element or
  // property.

  var objects = new WeakMap(); // object to path mappings

  return (function derez(value, path) {
    // The derez function recurses through the object, producing the deep copy.

    var old_path; // The path of an earlier occurance of value
    var nu; // The new object or array

    // If a replacer function was provided, then call it to get a replacement value.

    if (replacer !== undefined) {
      value = replacer(value);
    }

    // typeof null === "object", so go on if this value is really an object but not
    // one of the weird builtin objects.

    if (
      typeof value === 'object' &&
      value !== null &&
      !(value instanceof Boolean) &&
      !(value instanceof Date) &&
      !(value instanceof Number) &&
      !(value instanceof RegExp) &&
      !(value instanceof String)
    ) {
      // If the value is an object or array, look to see if we have already
      // encountered it. If so, return a {"$ref":PATH} object. This uses an
      // ES6 WeakMap.

      old_path = objects.get(value);
      if (old_path !== undefined) {
        return { $ref: old_path };
      }

      // Otherwise, accumulate the unique value and its path.

      objects.set(value, path);

      // If it is an array, replicate the array.

      if (Array.isArray(value)) {
        nu = [];
        value.forEach(function (element, i) {
          nu[i] = derez(element, path + '[' + i + ']');
        });
      } else {
        // If it is an object, replicate the object.

        nu = {};
        Object.keys(value).forEach(function (name) {
          nu[name] = derez(
            value[name],
            path + '[' + JSON.stringify(name) + ']'
          );
        });
      }
      return nu;
    }
    return value;
  })(object, '$');
}

export function catchError<T, E extends new (message?: string) => Error>(
  func: Function,
  errorsToCatch?: E[]
): [T, undefined] | [undefined, InstanceType<E>] {
  try {
    const data = func();
    return [data, undefined] as [T, undefined];
  } catch (error) {
    if (errorsToCatch === undefined) {
      return [undefined, error];
    }
    if (errorsToCatch.some(e => error instanceof e)) {
      return [undefined, error];
    }
    throw error;
  }
}

export function validateNumber(value: string, allowNegative = false): boolean {
  const positiveRegex = /^[0-9,]+([.][0-9]+)?$/;
  const negativeRegex = /^-?[0-9,]+([.][0-9]+)?$/;
  const regex = allowNegative ? negativeRegex : positiveRegex;

  return regex.test(value);
}

export async function catchErrorAsync<
  T,
  E extends new (message?: string) => Error
>(
  promise: Promise<T>,
  errorsToCatch?: E[]
): Promise<[T, undefined] | [undefined, InstanceType<E>]> {
  try {
    const data = await promise;
    return [data, undefined] as [T, undefined];
  } catch (error) {
    if (errorsToCatch === undefined) {
      return [undefined, error];
    }
    if (errorsToCatch.some(e => error instanceof e)) {
      return [undefined, error];
    }
    throw error;
  }
}

async function testCatchErrorAsync() {
  class TestError extends Error {}
  const [err, data] = await catchErrorAsync(
    Promise.reject(new TestError('test')),
    [TestError]
  );
  if (err) {
    console.error('caught', err);
  } else {
    console.log(data);
  }
}

async function testCatchError() {
  class TestError extends Error {}
  const [err, data] = catchError(() => {
    throw new TestError('test');
  }, [TestError]);
  if (err) {
    console.error('caught', err);
  } else {
    console.log(data);
  }
}
